import { useNavigate } from "react-router-dom";
import { MouseEvent, SyntheticEvent, useCallback, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Container,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import AppInput from "components/AppInput";
import useRowSelect from "../hooks/useRowSelect";
import useItemList from "../hooks/useItemList";
import SelectableTableHead, { HeadCell } from "./ItemTable";

/**
 * The ItemInfoList page
 */
export default function ItemInfoList() {
  const [{ total, itemList }] = useItemList();
  const [count, setCount] = useState(2);
  const [isEmpty, setIsEmpty] = useState(false);
  const [idFilter, setIdFilter] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const [makerFilter, setMakerFilter] = useState("");
  const [widthFilter, setWidthFilter] = useState("");
  const navigate = useNavigate();
  const headCells: HeadCell[] = [
    {
      id: "id",
      label: "id",
    },
    {
      id: "brand",
      label: "ブランド",
    },
    {
      id: "width",
      label: "幅",
    },
    {
      id: "maker",
      label: "メーカー",
    },
    {
      id: "detail",
      label: "詳細",
    },
  ];
  const {
    selectedRowIds,
    isSelected,
    isSelectedAll,
    isIndeterminate,
    toggleSelected,
    toggleSelectedAll,
  } = useRowSelect(itemList.map((item) => item.id));

  const onClickProductsRegistrationLink = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      navigate("/item/:itemId/register");
    },
    [navigate],
  );

  const onClickProductsListLink = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      // console.log(e.currentTarget.getAttribute("data-id"))
      navigate(`/item/${e.currentTarget.getAttribute("data-id")}`, {
        state: { id: e.currentTarget.getAttribute("data-id") },
      });
    },
    [navigate],
  );

  const displayMore = () => {
    if (count > itemList.length) {
      setIsEmpty(true);
    } else {
      setCount(count + 2);
    }
  };

  const filteredItemList = itemList.filter(
    (item) =>
      item.id.toString().includes(idFilter) &&
      item.brand.toLowerCase().includes(brandFilter.toLowerCase()) &&
      item.width.toString().includes(widthFilter) &&
      item.maker.toLowerCase().includes(makerFilter.toLowerCase()),
  );

  type OptionType = {
    labelName: string;
  };

  const Options: OptionType[] = [{ labelName: "レグノ GR-Ⅶ" }, { labelName: "アドバン AD09" }];

  const setBrandFilterWrapper = (_e: SyntheticEvent<Element, Event>, newValue: any) => {
    // const element: HTMLElement = document.getElementById("select") as HTMLElement
    // const value: string = element?.getAttribute('value') as string
    setBrandFilter(newValue?.labelName || "");
  };

  return (
    <div>
      <h2>
        <Typography fontSize={22}>商品一覧</Typography>
      </h2>
      <Box sx={{ flexGrow: 1, padding: 2 }}>
        <Grid container>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <AppInput
              labelName="ID"
              type="number"
              value={idFilter}
              onChange={(e) => setIdFilter(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Autocomplete
              options={Options}
              onChange={setBrandFilterWrapper}
              getOptionLabel={(option) => option.labelName}
              isOptionEqualToValue={(option, value) => option.labelName === value.labelName}
              renderInput={(params) => (
                <AppInput
                  {...params}
                  labelName="ブランド"
                  type="string"
                  value={brandFilter}
                  onChange={(e) => setBrandFilter(e.target.value)}
                  inputProps={{
                    ...params.InputProps,
                    inputProps: params.inputProps,
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <AppInput
              labelName="幅"
              type="number"
              value={widthFilter}
              onChange={(e) => setWidthFilter(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <AppInput
              labelName="メーカー"
              type="string"
              value={makerFilter}
              onChange={(e) => setMakerFilter(e.target.value)}
            />
          </Grid>
        </Grid>
      </Box>
      <Container>
        <TableContainer component={Table}>
          <Table>
            <SelectableTableHead
              onSelectAllClick={toggleSelectedAll}
              headCells={headCells}
              checked={isSelectedAll}
              indeterminate={isIndeterminate}
            />
            <TableBody>
              {filteredItemList.slice(0, count).map((item) => {
                const isItemSelected = isSelected(item.id);

                return (
                  <TableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={item.id}
                    onClick={() => toggleSelected(item.id)}
                    selected={isItemSelected}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox checked={isItemSelected} />
                    </TableCell>
                    <TableCell>{item.id}</TableCell>
                    <TableCell>{item.brand}</TableCell>
                    <TableCell>{item.width}</TableCell>
                    <TableCell>{item.maker}</TableCell>
                    <TableCell key={item.id}>
                      <IconButton onClick={onClickProductsListLink} data-id={item.id}>
                        <Edit />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
      <Button disabled={isEmpty} onClick={displayMore}>
        さらに表示
      </Button>
    </div>
  );
}
